import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as actions from '../actions/index';
import { Observable, catchError, exhaustMap, map, of, switchMap } from 'rxjs';
import {
    PermPostOfferLinks,
    PermPostOfferService
} from 'src/app/accountmanagers/perm-post-offer/services/perm-post-offer.service';
import {
    PermPostOfferBillingUpdatePayload,
    PostOfferBillingInformation
} from '../models/perm-post-offer-billing-information.model';
import { PermPostOfferWorkflowModalManagerService } from 'src/app/accountmanagers/perm-post-offer/services/perm-post-offer-workflow-modal-manager.service';
import { PermOfferBillingUpdateModalComponent } from 'src/app/accountmanagers/submittals/am-submittal-status-update/perm-offer-billing-update-modal/perm-offer-billing-update-modal.component';
import { TypedAction } from '@ngrx/store/src/models';
import { PermanentCandidateItemModel } from 'src/app/shared/models/permanent-candidate-item.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { AccountManagersService } from 'src/app/accountmanagers/accountmanagers.service';
import { PermPostOfferOfferModel } from '../models/perm-post-offer-offer.model';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';

@Injectable()
export class PermPostOfferBillingUpdateEffects {
    openPermPostOfferBillingUpdateModalFromGrid$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(actions.openPermOfferBillingUpdateModalFromGrid),
            exhaustMap((action) =>
                this._accountManagerService
                    .getJobTitle(action.model.jobId)
                    .pipe(map((jobTitle) => this.createPostOfferBillingUpdateModal(action.model, jobTitle)))
            )
        );
    });

    updatePermPostOfferBillingInfo$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(actions.updatePermPostOfferBillingInfo),
            switchMap((action) =>
                this._permPostOfferService.updatePermPostOfferBillingInfo(action.model).pipe(
                    map((result: PostOfferBillingInformation) => {
                        this._toasterService.toast('Billing information is updated.');
                        return actions.updatePermPostOfferBillingInfoSuccess({ model: result });
                    }),
                    catchError((error) => of(actions.updatePermPostOfferBillingInfoFailure({ error })))
                )
            )
        );
    });

    updatePermPostOfferBillingInfoSuccess$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(actions.updatePermPostOfferBillingInfoSuccess),
            switchMap(() => {
                this._permPostOfferWorkflowModalManagerService.closeModal();
                return [actions.clearPostOfferBillingState(), actions.refreshPermanentCandidatesPreStartPage()];
            })
        );
    });

    updatePermPostOfferBillingInfoFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(actions.updatePermPostOfferBillingInfoFailure),
            map(() => {
                this._permPostOfferWorkflowModalManagerService.closeModal();
                return showToasterMessage({
                    message: 'Failed to update Billing information.'
                });
            })
        );
    });

    cancelPermPostOfferBillingInfoModal$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(actions.cancelPermPostOfferBillingInfoModal),
            switchMap(() => this.clearPostOfferBillingState())
        );
    });

    private createPostOfferBillingUpdateModal(
        actionPayload: PermanentCandidateItemModel,
        jobTitle: string
    ): TypedAction<'[Perm Post Offer Billing Update Modal Component] Load Perm Post Offer Billing Update Modal'> {
        const data = this.generatePermPostOfferBillingUpdatePayload(jobTitle, actionPayload);
        this._permPostOfferWorkflowModalManagerService.createModal(
            PermOfferBillingUpdateModalComponent,
            data,
            '700px',
            '700px'
        );
        return actions.loadPostOfferBillingUpdateModal();
    }

    private clearPostOfferBillingState(): Observable<
        TypedAction<'[Perm Post Offer Billing Update Modal Component] Clear Post Offer billing State'>
    > {
        this._permPostOfferWorkflowModalManagerService.closeModal();
        return of(actions.clearPostOfferBillingState());
    }

    private generatePermPostOfferBillingUpdatePayload(
        jobTitle: string,
        payload: PermanentCandidateItemModel
    ): PermPostOfferBillingUpdatePayload {
        const permPostOfferOfferModel: PermPostOfferOfferModel = {
            facilityName: payload.facilityName,
            facilityLink: PermPostOfferLinks.FacilityLink(payload.facilityId),
            candidateName: `${payload.candidateFirstName} ${payload.candidateLastName}`,
            candidateLink: PermPostOfferLinks.CandidateLinkByContractorStatus(
                payload.isSubcontractor,
                payload.candidateId
            ),
            jobName: jobTitle,
            jobLink: PermPostOfferLinks.JobLink(payload.jobId),
            displayOfferStatusSelection: false
        };
        const postOfferBillingInfo = {
            permanentJobOfferId: payload.permanentJobOfferId,
            billingAmount: payload.billingAmount,
            discountBillingAmount: payload.discountedBillingAmount,
            startDate: payload.startDate
        } satisfies PostOfferBillingInformation;

        return {
            permPostOfferOfferModel: permPostOfferOfferModel,
            postOfferBillingInformation: postOfferBillingInfo,
            offerDate: payload.offerDate
        } satisfies PermPostOfferBillingUpdatePayload;
    }

    constructor(
        private readonly _actions$: Actions,
        private readonly _permPostOfferService: PermPostOfferService,
        private readonly _permPostOfferWorkflowModalManagerService: PermPostOfferWorkflowModalManagerService,
        private readonly _toasterService: ToasterService,
        private readonly _accountManagerService: AccountManagersService
    ) {}
}
